import {
  Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, computed, inject, signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, Subject, catchError, debounceTime, forkJoin, interval, map, of, switchMap, tap } from 'rxjs';

import { CampoSelectComponent } from '../../shared/components/redesign/campo-select/campo-select.component';
import { CanalOrigemContatoEnum } from '../../shared/enum/canal-origem-contato.enum';
import { TipoPreviaMensagemEnum } from '../../shared/enum/tipo-previa-mensagem.enum';
import { ContatosChatResposta } from '../../shared/model/chat/contatos-chat-resposta.model';
import { Contato } from '../../shared/model/contato/contato.model';
import { AbaChatContatos, FiltroChatContatos } from '../../shared/model/contato/filtro-chat-contatos.model';
import { NovaMensagemContato } from '../../shared/model/contato/nova-mensagem-contato.model';
import { IdNome } from '../../shared/model/id-nome.model';
import { UsuarioLogado } from '../../shared/model/usuario/usuario-logado.model';
import { AuthService } from '../../shared/service/auth.service';
import { CanalService } from '../../shared/service/canal.service';
import { ContatoService } from '../../shared/service/contato.service';
import { MsgService } from '../../shared/service/msg.service';
import { SocketService } from '../../shared/service/socket.service';
import { UsuarioService } from '../../shared/service/usuario.service';
import { CHAT_TRANSFERENCIA_STORAGE_KEY, CONTATO_DISPARO_FILTRO_SESSION_KEY } from '../../shared/util/chat-transferencia.constants';
import { AberturaContatoService } from '../../shared/service/abertura-contato.service';
import { MethodsUtils } from '../../shared/util/methods.utils';
import { animacaoExpandir, animacaoSurgir } from '../utils/chat-animacoes.utils';
import { ArrastarAbasDirective } from '../utils/arrastar-abas.directive';
import { PreviaMensagemComponent } from './previa-mensagem/previa-mensagem.component';
import { AvatarPessoaComponent } from '../../shared/components/redesign/avatar-pessoa/avatar-pessoa.component';
import { AbaChat } from './models/aba-chat.model';
import { BuscaPagina } from './models/busca-pagina.model';
import { LinhaListaContatos } from './models/linha-lista-contatos.model';
import { ModoListaContatos } from './models/modo-lista-contatos.model';
import { ContatoCriado } from '../../contato/modal-cadastro-contato/models/contato-criado.model';
import {
  ALTURA_ITEM_CONTATO, DURACAO_DESTAQUE_CONTATO_NOVO, DURACAO_SAIDA_CONTATO_TEMPORARIO, LINHAS_SKELETON_MAIS, MAX_TAGS_VISIVEIS, PASTA_ICONES_FILTRO, TAMANHO_PAGINA, TEXTO_VAZIO_CHAT,
  TEXTO_VAZIO_CONTATOS
} from './utils/lista-contatos-chat.constantes';

@Component({
  selector: 'app-lista-contatos-chat',
  standalone: true,
  imports: [FormsModule, MatTooltip, CampoSelectComponent, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf,
    ArrastarAbasDirective, PreviaMensagemComponent, AvatarPessoaComponent],
  templateUrl: './lista-contatos-chat.component.html',
  styleUrl: './lista-contatos-chat.component.css',
  animations: [animacaoExpandir, animacaoSurgir]
})
export class ListaContatosChatComponent implements OnInit {
  protected readonly CanalOrigemContatoEnum = CanalOrigemContatoEnum;
  protected readonly linhasSkeleton = [1, 2, 3, 4, 5, 6, 7, 8];
  protected readonly alturaItem = ALTURA_ITEM_CONTATO;

  @Input() set contatoSelecionadoId(id: number | undefined) {
    const anterior = this.selecionadoId;
    this.selecionadoId = id;
    if (anterior != null && anterior !== id && this.contatosTemporarios.has(anterior)) this.iniciarSaidaTemporario(anterior);
    if (id != null) this.cancelarSaidaTemporario(id);
  }

  get contatoSelecionadoId(): number | undefined {
    return this.selecionadoId;
  }

  @Input() modo: ModoListaContatos = 'CHAT';
  @Output() contatoSelecionado = new EventEmitter<Contato>();
  @Output() abrirMenu = new EventEmitter<void>();
  @Output() criarContato = new EventEmitter<void>();

  @ViewChild('campoBusca') campoBusca?: ElementRef<HTMLInputElement>;
  @ViewChild('areaItens', { read: ElementRef }) areaItens?: ElementRef<HTMLElement>;

  readonly abas: AbaChat[] = [
    { valor: 'TODOS', label: 'Todas', iconeAtivo: `${PASTA_ICONES_FILTRO}/filter-all-primary.svg`, iconeInativo: `${PASTA_ICONES_FILTRO}/filter-all-gray.svg` },
    { valor: 'NAO_LIDOS', label: 'Não lidas', iconeAtivo: `${PASTA_ICONES_FILTRO}/filter-nao-lida-primary.svg`, iconeInativo: `${PASTA_ICONES_FILTRO}/filter-nao-lida-gray.svg` },
    { valor: 'FAVORITOS', label: 'Importantes', iconeAtivo: `${PASTA_ICONES_FILTRO}/filter-fav-golden.svg`, iconeInativo: `${PASTA_ICONES_FILTRO}/filter-fav-gray.svg` },
  ];

  aba = signal<AbaChatContatos>('TODOS');
  contatos = signal<Contato[]>([]);
  qtdTodos = signal(0);
  qtdNaoLidos = signal(0);
  qtdFavoritos = signal(0);
  carregando = signal(true);
  buscando = signal(false);
  carregandoMais = signal(false);
  temProximaPagina = signal(false);
  mostrarTopo = signal(false);
  contatosNovos = signal<Set<number>>(new Set());
  contatosSaindo = signal<ReadonlySet<number>>(new Set());
  private selecionadoId?: number;
  private buscaContatoDisparo?: string;
  private readonly contatosTemporarios = new Set<number>();
  private readonly timersSaida = new Map<number, ReturnType<typeof setTimeout>>();

  textoFiltro = '';
  buscaAberta = signal(false);
  filtrosAbertos = signal(false);

  canais = signal<IdNome[]>([]);
  operadores = signal<IdNome[]>([]);
  carregandoOpcoesFiltro = signal(false);
  canaisSelecionados = signal<IdNome[]>([]);
  operadoresSelecionados = signal<IdNome[]>([]);

  private readonly agora = signal(Date.now());

  readonly linhasLista = computed<LinhaListaContatos[]>(() => {
    const linhas = this.contatos().map(contato => ({ chave: `c-${contato.id}`, contato }));
    return this.carregandoMais() ? [...linhas, ...LINHAS_SKELETON_MAIS] : linhas;
  });

  readonly temFiltroAvancado = computed(() => this.canaisSelecionados().length > 0 || this.operadoresSelecionados().length > 0);
  readonly chipCanais = computed(() => this.montarChip('Canal', this.canaisSelecionados(), this.canais()));
  readonly chipOperadores = computed(() => this.montarChip('Operador', this.operadoresSelecionados(), this.operadores()));

  readonly usuarioLogado: UsuarioLogado;
  readonly exibeFiltroOperador: boolean;

  private pagina = 0;
  private recontagemPendente = false;
  private opcoesFiltroCarregadas = false;
  private ultimoSom = 0;
  private readonly busca$ = new Subject<BuscaPagina>();
  private readonly textoFiltro$ = new Subject<string>();

  private readonly contatoService = inject(ContatoService);
  private readonly canalService = inject(CanalService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly socketService = inject(SocketService);
  private readonly authService = inject(AuthService);
  private readonly msgService = inject(MsgService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly aberturaContatoService = inject(AberturaContatoService);

  constructor() {
    this.usuarioLogado = this.authService.getUsuarioLogadoClariBot() ?? new UsuarioLogado();
    this.exibeFiltroOperador = !this.usuarioLogado.isOperador;
  }

  ngOnInit(): void {
    if (this.ehTelaContatos()) {
      this.aplicarFiltroContatoDisparo();
      this.aberturaContatoService.pedidos$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.aplicarFiltroContatoDisparo();
          this.recarregar();
        });
    } else {
      this.aplicarFiltroTransferenciaPendente();
    }

    this.busca$
      .pipe(
        switchMap(({ pagina, anexar, contar }) =>
          this.buscarPagina(this.montarFiltro(), pagina, contar || this.recontagemPendente).pipe(
            map(res => ({ res, pagina, anexar })),
            catchError(() => {
              this.msgService.msgErro(anexar ? 'Erro ao buscar mais contatos.' : 'Erro ao carregar a lista de contatos.');
              return of({ res: null, pagina, anexar });
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ res, pagina, anexar }) => this.aplicarResultado(res, pagina, anexar));

    this.textoFiltro$
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buscarPorTexto());

    this.socketService.connect();
    this.socketService.notificacaoListaContato
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => this.processarNotificacao(payload));

    interval(60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.agora.set(Date.now()));

    this.recarregar();
  }

  ehTelaContatos(): boolean {
    return this.modo === 'CONTATOS';
  }

  get textoVazio(): string {
    return this.ehTelaContatos() ? TEXTO_VAZIO_CONTATOS : TEXTO_VAZIO_CHAT;
  }

  recarregar(): void {
    this.limparTemporarios();
    this.recontagemPendente = true;
    this.pagina = 0;
    this.carregando.set(true);
    this.busca$.next({ pagina: 0, anexar: false, contar: true });
  }

  private buscarPorTexto(): void {
    this.pagina = 0;
    this.buscando.set(true);
    this.busca$.next({ pagina: 0, anexar: false, contar: false });
  }

  onScroll(): void {
    const el = this.areaItens?.nativeElement;
    if (el) this.mostrarTopo.set(el.scrollTop > el.clientHeight);
    this.verificarPreCarga();
  }

  rastrearLinha(_indice: number, linha: LinhaListaContatos): string {
    return linha.chave;
  }

  irAoTopo(): void {
    this.areaItens?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private verificarPreCarga(): void {
    const el = this.areaItens?.nativeElement;
    if (!el || this.carregando() || this.buscando() || this.carregandoMais() || !this.temProximaPagina()) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight * 3) {
      this.carregandoMais.set(true);
      this.busca$.next({ pagina: this.pagina + 1, anexar: true, contar: false });
    }
  }

  private aplicarResultado(res: ContatosChatResposta | null, pagina: number, anexar: boolean): void {
    this.carregando.set(false);
    this.buscando.set(false);
    this.carregandoMais.set(false);
    if (!res) return;

    const contatos = (res.contatos ?? []).map(c => this.formatarContato(c));
    this.pagina = pagina;
    this.contatos.set(anexar ? this.mesclarSemDuplicar(this.contatos(), contatos) : contatos);
    if (!anexar) this.selecionarContatoDoDisparo(contatos);
    if (res.qtdTodos != null) {
      this.qtdTodos.set(res.qtdTodos);
      this.recontagemPendente = false;
    }
    if (res.qtdNaoLidos != null) this.qtdNaoLidos.set(res.qtdNaoLidos);
    if (res.qtdFavoritos != null) this.qtdFavoritos.set(res.qtdFavoritos);
    this.temProximaPagina.set(!!res.temProximaPagina);
    setTimeout(() => this.verificarPreCarga(), 0);
  }

  private buscarPagina(filtro: FiltroChatContatos, pagina: number, contar: boolean): Observable<ContatosChatResposta> {
    return this.ehTelaContatos()
      ? this.contatoService.buscarContatosTelaContatos(filtro, pagina, TAMANHO_PAGINA, contar)
      : this.contatoService.buscarContatosChat(filtro, pagina, TAMANHO_PAGINA, contar);
  }

  private mesclarSemDuplicar(atuais: Contato[], novos: Contato[]): Contato[] {
    const ids = new Set(atuais.map(c => c.id));
    return [...atuais, ...novos.filter(c => !ids.has(c.id))];
  }

  private montarFiltro(): FiltroChatContatos {
    const filtro = new FiltroChatContatos();
    filtro.textoFiltro = this.textoFiltro.trim();
    filtro.aba = this.aba();
    filtro.idsCanais = this.canaisSelecionados().map(c => c.id).filter((id): id is number => id != null);
    filtro.idsOperadores = this.operadoresSelecionados().map(o => o.id).filter((id): id is number => id != null);
    return filtro;
  }

  private aplicarFiltroTransferenciaPendente(): void {
    const numeroContato = localStorage.getItem(CHAT_TRANSFERENCIA_STORAGE_KEY);
    if (!numeroContato) return;
    this.textoFiltro = numeroContato;
    this.buscaAberta.set(true);
    localStorage.removeItem(CHAT_TRANSFERENCIA_STORAGE_KEY);
  }

  private aplicarFiltroContatoDisparo(): void {
    const busca = sessionStorage.getItem(CONTATO_DISPARO_FILTRO_SESSION_KEY);
    if (!busca) return;
    sessionStorage.removeItem(CONTATO_DISPARO_FILTRO_SESSION_KEY);
    this.textoFiltro = busca;
    this.buscaAberta.set(true);
    this.buscaContatoDisparo = busca;
  }

  private selecionarContatoDoDisparo(contatos: Contato[]): void {
    const busca = this.buscaContatoDisparo;
    if (!busca) return;
    this.buscaContatoDisparo = undefined;
    const contato = contatos.find(c => this.correspondeBuscaDisparo(c, busca)) ?? (contatos.length === 1 ? contatos[0] : undefined);
    if (contato) this.selecionarContato(contato);
  }

  private correspondeBuscaDisparo(contato: Contato, busca: string): boolean {
    const digitos = busca.replace(/\D/g, '');
    return !!digitos && contato.numero?.replace(/\D/g, '') === digitos;
  }

  trocarAbaRelativa(passo: number): void {
    const indice = this.abas.findIndex(a => a.valor === this.aba());
    const destino = this.abas[indice + passo];
    if (destino) this.selecionarAba(destino.valor);
  }

  selecionarAba(aba: AbaChatContatos): void {
    if (aba === this.aba()) return;
    this.aba.set(aba);
    this.recarregar();
  }

  contadorAba(aba: AbaChatContatos): number {
    switch (aba) {
      case 'NAO_LIDOS': return this.qtdNaoLidos();
      case 'FAVORITOS': return this.qtdFavoritos();
      default: return this.qtdTodos();
    }
  }

  contadorAbaAbreviado(aba: AbaChatContatos): string {
    return MethodsUtils.abreviarNumero(this.contadorAba(aba));
  }

  abrirBusca(): void {
    if (this.buscaAberta()) return;
    this.buscaAberta.set(true);
    setTimeout(() => this.campoBusca?.nativeElement.focus(), 60);
  }

  @HostListener('document:click', ['$event'])
  aoClicarFora(event: MouseEvent): void {
    if (!this.buscaAberta() || this.textoFiltro.trim()) return;
    const busca = this.elementRef.nativeElement.querySelector('.lista-chat-busca');
    if (busca && !busca.contains(event.target as Node)) this.buscaAberta.set(false);
  }

  fecharBusca(): void {
    this.buscaAberta.set(false);
    if (this.textoFiltro) {
      this.textoFiltro = '';
      this.buscarPorTexto();
    }
  }

  aoDigitarBusca(): void {
    this.textoFiltro$.next(this.textoFiltro);
  }

  alternarFiltros(): void {
    this.filtrosAbertos.update(v => !v);
    if (this.filtrosAbertos()) {
      this.carregarOpcoesFiltro();
    }
  }

  selecionarCanais(canais: IdNome[]): void {
    this.canaisSelecionados.set([...(canais ?? [])]);
    this.recarregar();
  }

  selecionarOperadores(operadores: IdNome[]): void {
    this.operadoresSelecionados.set([...(operadores ?? [])]);
    this.recarregar();
  }

  limparFiltroCanais(): void {
    this.canaisSelecionados.set([]);
    this.recarregar();
  }

  limparFiltroOperadores(): void {
    this.operadoresSelecionados.set([]);
    this.recarregar();
  }

  private carregarOpcoesFiltro(): void {
    if (this.opcoesFiltroCarregadas) return;
    this.opcoesFiltroCarregadas = true;
    this.carregandoOpcoesFiltro.set(true);

    forkJoin({
      canais: this.canalService.buscarCanaisAtivoDoGrupoPeloContatoUsuario().pipe(catchError(() => of([] as IdNome[]))),
      operadores: this.exibeFiltroOperador
        ? this.usuarioService.buscarOperadoresPorGrupo().pipe(catchError(() => of([])))
        : of([])
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ canais, operadores }) => {
        this.canais.set((canais ?? []).filter(c => c?.id != null && c.id !== 0));
        this.operadores.set((operadores ?? []).map((o: any) => ({ id: o.id, nome: o.nome } as IdNome)));
        this.carregandoOpcoesFiltro.set(false);
      });
  }

  private montarChip(prefixo: string, itens: IdNome[], opcoes: IdNome[]): { texto: string; extras: number; titulo: string } | null {
    if (!itens.length) return null;
    if (opcoes.length > 1 && itens.length === opcoes.length) {
      return { texto: `${prefixo}: Todos`, extras: 0, titulo: `${prefixo}: Todos` };
    }
    return {
      texto: `${prefixo}: ${itens[0].nome ?? ''}`,
      extras: itens.length - 1,
      titulo: `${prefixo}: ${itens.map(i => i.nome).join(', ')}`
    };
  }

  selecionarContato(contato: Contato): void {
    const tinhaNaoLidas = (contato.qtdMensagensNaoLidas ?? 0) > 0;
    if (this.ehOperadorDaConversa(contato) && (tinhaNaoLidas || contato.tranferenciaPendenteVisu)) {
      contato.qtdMensagensNaoLidas = 0;
      contato.tranferenciaPendenteVisu = false;
      if (tinhaNaoLidas) {
        this.qtdNaoLidos.update(v => Math.max(0, v - 1));
      }
      this.contatoService.visualizarContato(contato).subscribe();
      this.contatos.set([...this.contatos()]);
    }
    this.contatoSelecionado.emit(contato);
  }

  aplicarFavorito(contatoId: number, favorito: boolean): void {
    const contato = this.contatos().find(c => c.id === contatoId);
    this.qtdFavoritos.update(v => Math.max(0, v + (favorito ? 1 : -1)));
    if (this.aba() === 'FAVORITOS' && !favorito) {
      this.contatos.set(this.contatos().filter(c => c.id !== contatoId));
      return;
    }
    if (contato) {
      contato.favorito = favorito;
      this.contatos.set([...this.contatos()]);
    }
  }

  recarregarContato(contatoId: number, numero?: string): Observable<Contato | null | undefined> {
    const numeroBusca = this.contatos().find(c => c.id === contatoId)?.contato ?? numero;
    if (!numeroBusca) return of(undefined);

    const filtro = this.montarFiltro();
    filtro.textoFiltro = numeroBusca;
    return this.buscarPagina(filtro, 0, false).pipe(
      map(res => {
        const encontrado = (res?.contatos ?? []).find(c => c.id === contatoId);
        return encontrado ? this.formatarContato(encontrado) : null;
      }),
      tap(contato => this.aplicarContatoRecarregado(contatoId, contato)),
      catchError(() => of(undefined))
    );
  }

  private aplicarContatoRecarregado(contatoId: number, contato: Contato | null): void {
    if (contato) {
      this.substituirContato(contato);
    } else if (!this.ehTelaContatos()) {
      this.removerContato(contatoId);
    }
  }

  adicionarContatoCriado(criado: ContatoCriado): Observable<Contato> {
    const filtro = new FiltroChatContatos();
    filtro.textoFiltro = criado.numero;
    return this.contatoService.buscarContatosTelaContatos(filtro, 0, TAMANHO_PAGINA, false).pipe(
      map(res => (res?.contatos ?? []).find(c => c.id === criado.id)),
      catchError(() => of(undefined)),
      map(encontrado => this.formatarContato(encontrado ?? this.montarContatoCriado(criado))),
      tap(contato => {
        this.contatosTemporarios.add(contato.id);
        this.contatos.set([contato, ...this.contatos().filter(c => c.id !== contato.id)]);
        this.destacarContatoNovo(contato.id);
        this.areaItens?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
      })
    );
  }

  private montarContatoCriado(criado: ContatoCriado): Contato {
    const contato = new Contato();
    contato.id = criado.id;
    contato.nome = criado.nome;
    contato.contato = criado.numero;
    contato.numero = criado.numero;
    contato.qtdMensagensNaoLidas = 0;
    contato.tags = [];
    return contato;
  }

  private iniciarSaidaTemporario(contatoId: number): void {
    this.cancelarSaidaTemporario(contatoId);
    this.contatosSaindo.update(ids => new Set(ids).add(contatoId));
    this.timersSaida.set(contatoId, setTimeout(() => this.removerTemporario(contatoId), DURACAO_SAIDA_CONTATO_TEMPORARIO));
  }

  private cancelarSaidaTemporario(contatoId: number): void {
    const timer = this.timersSaida.get(contatoId);
    if (timer) clearTimeout(timer);
    this.timersSaida.delete(contatoId);
    if (!this.contatosSaindo().has(contatoId)) return;
    this.contatosSaindo.update(ids => {
      const restantes = new Set(ids);
      restantes.delete(contatoId);
      return restantes;
    });
  }

  private removerTemporario(contatoId: number): void {
    this.cancelarSaidaTemporario(contatoId);
    if (!this.contatosTemporarios.has(contatoId) || contatoId === this.selecionadoId) return;
    this.contatosTemporarios.delete(contatoId);
    this.contatos.set(this.contatos().filter(c => c.id !== contatoId));
  }

  private efetivarTemporario(contatoId: number): void {
    if (!this.contatosTemporarios.delete(contatoId)) return;
    this.cancelarSaidaTemporario(contatoId);
  }

  private limparTemporarios(): void {
    this.timersSaida.forEach(timer => clearTimeout(timer));
    this.timersSaida.clear();
    this.contatosTemporarios.clear();
    this.contatosSaindo.set(new Set());
  }

  private substituirContato(contato: Contato): void {
    const lista = this.contatos();
    const indice = lista.findIndex(c => c.id === contato.id);
    if (indice < 0) return;
    const copia = [...lista];
    copia[indice] = contato;
    this.contatos.set(copia);
  }

  removerContato(contatoId: number): void {
    const contato = this.contatos().find(c => c.id === contatoId);
    if (!contato) return;
    this.contatos.set(this.contatos().filter(c => c.id !== contatoId));
    this.qtdTodos.update(v => Math.max(0, v - 1));
    if ((contato.qtdMensagensNaoLidas ?? 0) > 0) this.qtdNaoLidos.update(v => Math.max(0, v - 1));
    if (contato.favorito) this.qtdFavoritos.update(v => Math.max(0, v - 1));
  }

  private processarNotificacao(payload: NovaMensagemContato): void {
    const novo = payload?.contato;
    if (payload?.atendimentoAlterado && novo?.id) {
      this.processarAtendimentoAlterado(novo);
      return;
    }
    if (!novo?.id) return;
    if (!(payload.novaMensagemRecebida || payload.novaMensagemEnviada)) {
      this.atualizarPreviaContato(novo);
      return;
    }

    const lista = this.contatos();
    const existente = lista.find(c => c.id === novo.id);
    const ehSelecionado = novo.id === this.contatoSelecionadoId;
    if (existente) {
      this.efetivarTemporario(novo.id);
      MethodsUtils.copiarEstadoAtendimento(novo, existente);
    }

    if (!this.podeVisualizar(novo)) {
      if (existente && !ehSelecionado) this.removerContato(novo.id);
      return;
    }

    const contato = this.formatarContato(novo);
    contato.favorito = existente?.favorito ?? false;
    if (ehSelecionado && this.ehOperadorDaConversa(contato) && (contato.qtdMensagensNaoLidas ?? 0) > 0) {
      contato.qtdMensagensNaoLidas = 0;
      this.contatoService.salvarMsgLida(contato.id).subscribe();
    }

    if (existente && contato.ultimaMensagemPrevia?.tipo === TipoPreviaMensagemEnum.REACAO) {
      this.substituirContato(contato);
      return;
    }

    if (!existente && !this.correspondeAosFiltros(contato)) {
      if (payload.novaMensagemRecebida && this.visivelNoChat(novo)) this.tocarSomNotificacao();
      return;
    }

    const eraNaoLido = (existente?.qtdMensagensNaoLidas ?? 0) > 0;
    const ehNaoLido = (contato.qtdMensagensNaoLidas ?? 0) > 0;
    if (eraNaoLido !== ehNaoLido) this.qtdNaoLidos.update(v => Math.max(0, v + (ehNaoLido ? 1 : -1)));
    if (!existente && !this.ehTelaContatos()) this.qtdTodos.update(v => v + 1);

    const semEle = lista.filter(c => c.id !== contato.id);
    const saiDaAba = this.aba() === 'NAO_LIDOS' && !ehNaoLido && !ehSelecionado;
    this.contatos.set(saiDaAba ? semEle : [contato, ...semEle]);
    if (!saiDaAba && (!existente || this.ehTelaContatos())) this.destacarContatoNovo(contato.id);

    if (payload.novaMensagemRecebida && this.visivelNoChat(novo)) this.tocarSomNotificacao();
  }

  private atualizarPreviaContato(novo: Contato): void {
    const existente = this.contatos().find(c => c.id === novo.id);
    if (!existente || !novo.ultimaMensagemPrevia) return;
    const atualizado = Object.assign(new Contato(), existente, {
      ultimaMensagemPrevia: novo.ultimaMensagemPrevia,
      ultimaMensagem: novo.ultimaMensagem,
      ultimaMensagemFormatada: false
    });
    this.substituirContato(this.formatarContato(atualizado));
  }

  private processarAtendimentoAlterado(novo: Contato): void {
    const existente = this.contatos().find(c => c.id === novo.id);
    const ehSelecionado = novo.id === this.contatoSelecionadoId;
    if (existente) this.efetivarTemporario(novo.id);

    if (!this.podeVisualizar(novo)) {
      if (existente && !ehSelecionado) this.removerContato(novo.id);
      return;
    }

    const contato = this.formatarContato(novo);
    contato.favorito = existente?.favorito ?? false;
    if (existente) {
      const eraNaoLido = (existente.qtdMensagensNaoLidas ?? 0) > 0;
      const ehNaoLido = (contato.qtdMensagensNaoLidas ?? 0) > 0;
      if (eraNaoLido !== ehNaoLido) this.qtdNaoLidos.update(v => Math.max(0, v + (ehNaoLido ? 1 : -1)));
      this.substituirContato(contato);
      return;
    }
    if (this.ehTelaContatos() || !this.correspondeAosFiltros(contato)) return;

    this.qtdTodos.update(v => v + 1);
    if ((contato.qtdMensagensNaoLidas ?? 0) > 0) this.qtdNaoLidos.update(v => v + 1);
    this.contatos.set([contato, ...this.contatos()]);
    this.destacarContatoNovo(contato.id);
  }

  private destacarContatoNovo(contatoId: number): void {
    this.contatosNovos.update(ids => new Set(ids).add(contatoId));
    setTimeout(() => this.contatosNovos.update(ids => {
      const restantes = new Set(ids);
      restantes.delete(contatoId);
      return restantes;
    }), DURACAO_DESTAQUE_CONTATO_NOVO);
  }

  private ehOperadorDaConversa(contato: Contato): boolean {
    return this.usuarioLogado.id != null && contato.operador?.id === this.usuarioLogado.id;
  }

  private podeVisualizar(contato: Contato): boolean {
    const usuario = this.usuarioLogado;
    if (contato.grupoId != null && usuario.grupo != null && Number(contato.grupoId) !== Number(usuario.grupo)) {
      return false;
    }
    return this.ehTelaContatos() || this.visivelNoChat(contato);
  }

  private visivelNoChat(contato: Contato): boolean {
    const usuario = this.usuarioLogado;
    const acesso = contato.acessoChat;
    if (!acesso || usuario.id == null) return false;
    if (usuario.isCoordenador) return (acesso.idsCoordenadores ?? []).includes(usuario.id);
    if (usuario.isOperador) return (acesso.idsOperadores ?? []).includes(usuario.id);
    return !!acesso.visivelUsuarios;
  }

  private correspondeAosFiltros(contato: Contato): boolean {
    if (this.aba() === 'FAVORITOS') return false;
    if (this.aba() === 'NAO_LIDOS' && !((contato.qtdMensagensNaoLidas ?? 0) > 0)) return false;

    const texto = this.textoFiltro.trim().toLowerCase();
    if (texto) {
      const digitos = texto.replace(/\D/g, '');
      const bate = (contato.nome ?? '').toLowerCase().includes(texto)
        || (digitos.length >= 3 && (contato.contato ?? '').includes(digitos))
        || String(contato.id) === texto;
      if (!bate) return false;
    }
    const idsCanais = this.canaisSelecionados().map(c => c.id);
    if (idsCanais.length && !idsCanais.includes(contato.canal?.id as number)) return false;
    const idsOperadores = this.operadoresSelecionados().map(o => o.id);
    if (idsOperadores.length && !idsOperadores.includes(contato.operador?.id as number)) return false;
    return true;
  }

  private tocarSomNotificacao(): void {
    const agora = Date.now();
    if (agora - this.ultimoSom < 3000) return;
    this.ultimoSom = agora;
    const audio = new Audio('./assets/audios/audioAttContato.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => { /**/ });
  }

  private formatarContato(contato: Contato): Contato {
    if (contato.ultimaMensagem && !contato.ultimaMensagemFormatada) {
      contato.ultimaMensagem = MethodsUtils.formatarMensagemComNegritoItalicoRiscada(contato.ultimaMensagem, true);
      contato.ultimaMensagemFormatada = true;
    }
    contato.qtdMensagensNaoLidas = contato.qtdMensagensNaoLidas ?? 0;
    contato.operadorAvatar = contato.operador?.id
      ? { id: contato.operador.id, nome: contato.operador.nome, uuid: contato.operador.uuid, extensaoImg: contato.operador.extensao }
      : undefined;
    return contato;
  }

  tempoUltimaMensagem(data?: Date | string): string {
    if (!data) return '';
    const momento = new Date(data);
    if (isNaN(momento.getTime())) return '';

    const minutos = Math.floor((this.agora() - momento.getTime()) / 60_000);
    if (minutos < 1) return 'Agora';
    if (minutos < 60) return `${minutos} min atrás`;

    const hoje = new Date(this.agora());
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (momento.toDateString() === hoje.toDateString()) {
      return momento.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (momento.toDateString() === ontem.toDateString()) return 'Ontem';
    return momento.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  temLinhaTags(contato: Contato): boolean {
    return !!contato.canal?.nome || !!contato.tags?.length || (contato.qtdMensagensNaoLidas ?? 0) > 0;
  }

  tagsVisiveis(contato: Contato): string[] {
    return (contato.tags ?? []).slice(0, MAX_TAGS_VISIVEIS);
  }

  tagsExcedentes(contato: Contato): number {
    return Math.max(0, (contato.tags?.length ?? 0) - MAX_TAGS_VISIVEIS);
  }

  tooltipTagsExcedentes(contato: Contato): string {
    return (contato.tags ?? []).slice(MAX_TAGS_VISIVEIS).join(', ');
  }

  textoContador(qtd: number): string {
    return qtd > 99 ? '99+' : String(qtd);
  }

  fotoContato(contato: Contato): string {
    return MethodsUtils.fotoPerfilContato(contato.fotoPerfilUrl, contato.statusContato);
  }

  possuiFotoPerfil(contato: Contato): boolean {
    return MethodsUtils.fotoPerfilDisponivel(contato.fotoPerfilUrl);
  }

  marcarFotoIndisponivel(contato: Contato): void {
    MethodsUtils.marcarFotoPerfilIndisponivel(contato.fotoPerfilUrl);
  }

  tooltipFotoContato(contato: Contato): string {
    return MethodsUtils.informacoesPorStatusContato(contato.statusContato, false);
  }

  logoCanalOrigem(contato: Contato): string | null {
    switch (contato.canalOrigem) {
      case CanalOrigemContatoEnum.INSTAGRAM:
        return './assets/logos/Instagram_logo_2022.svg.webp';
      case CanalOrigemContatoEnum.WHATSAPP:
        return './assets/logos/wpp-logo.png';
      default:
        return null;
    }
  }
}
